import { coreApiClient } from '../core-client';
import type {
  LoginInput,
  LoginResponse,
  RefreshTokenResponse,
  GetProfileResponse,
  SwitchBrandResponse,
  UserProfile,
  AuthTokens,
  ApiSuccess,
  ForgotPasswordResponse,
  ValidateTokenResponse,
  SetPasswordResponse,
} from '@bstock/shared';
import type { ModuleAccess } from '@bill/core-types';

export const authService = {
  /**
   * Login - Connexion utilisateur
   */
  async login(data: LoginInput): Promise<{ user: UserProfile; tokens: AuthTokens }> {
    const response = await coreApiClient.post<ApiSuccess<LoginResponse>>('/auth/login', data);
    return {
      user: response.data.data.user,
      tokens: response.data.data.tokens,
    };
  },

  /**
   * Refresh - Renouveler les tokens
   */
  async refresh(refreshToken: string): Promise<AuthTokens> {
    const response = await coreApiClient.post<ApiSuccess<RefreshTokenResponse>>('/auth/refresh', {
      refreshToken,
    });
    return response.data.data.tokens;
  },

  /**
   * Logout - Deconnexion (revoque le refresh token)
   */
  async logout(refreshToken: string): Promise<void> {
    await coreApiClient.post('/auth/logout', { refreshToken });
  },

  /**
   * Me - Recuperer le profil + l'acces module sur la brand active.
   * Le backend retourne `{ user, moduleAccess }` depuis US-MAC.3.
   * `moduleAccess` peut etre absent (legacy/old backend) — on tolere null.
   */
  async me(): Promise<{ user: UserProfile; moduleAccess: ModuleAccess | null }> {
    const response = await coreApiClient.get<
      ApiSuccess<GetProfileResponse & { moduleAccess?: ModuleAccess }>
    >('/auth/me');
    return {
      user: response.data.data.user,
      moduleAccess: response.data.data.moduleAccess ?? null,
    };
  },

  /**
   * Switch Brand - Changer de brand active
   */
  async switchBrand(brandId: string): Promise<AuthTokens> {
    const response = await coreApiClient.post<ApiSuccess<SwitchBrandResponse>>('/auth/switch-brand', {
      brandId,
    });
    return response.data.data.tokens;
  },

  /**
   * Forgot Password - Demande de reinitialisation de mot de passe
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await coreApiClient.post<{ status: string; message: string }>('/auth/forgot-password', {
      email,
    });
    return { message: response.data.message };
  },

  /**
   * Validate Token - Valider un token d'activation ou de reset
   */
  async validateToken(token: string): Promise<ValidateTokenResponse> {
    const response = await coreApiClient.get<ApiSuccess<ValidateTokenResponse>>('/auth/validate-token', {
      params: { token },
    });
    return response.data.data;
  },

  /**
   * Set Password - Definir le mot de passe (activation ou reset)
   */
  async setPassword(token: string, password: string): Promise<SetPasswordResponse> {
    const response = await coreApiClient.post<ApiSuccess<SetPasswordResponse>>('/auth/set-password', {
      token,
      password,
    });
    return response.data.data;
  },
};
